// Legal 문서 공용 페이지 셸 — BE 약관 본문(markdown)을 다크 테마 article 로 렌더.
// terms / privacy-policy / pi-consent / instagram-data-deletion 이 공유하며,
// SSG prerender(entry-server)도 같은 컴포넌트를 렌더하므로 여기 수정이 곧 정적 HTML 에도 반영된다.
//
// 본문 출처는 BE `GET /terms` 하나뿐이다 — 랜딩에 정적 사본은 없다 (src/lib/termsDocs.ts 참조).
// 앱에서 문서 뷰로 여는 페이지라 헤더·푸터 없이 본문만 둔다.
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getTermsDoc } from '@/lib/termsDocs';

type Props = {
  lang?: 'en' | 'ko';
  /** BE Terms.type 매핑 키이자 하단 언어 cross-link(`/{lang}/{slug}`) 경로 */
  slug: string;
};

export default function LegalDocPage({ lang, slug }: Props) {
  const { i18n } = useTranslation();
  const effective: 'en' | 'ko' = lang ?? (i18n.language === 'ko' ? 'ko' : 'en');
  const body = getTermsDoc(slug) ?? '';

  return (
    <div style={{ background: '#000', minHeight: '100vh' }}>
      <main className="max-w-[880px] mx-auto px-6 md:px-10 pt-16 pb-24 text-white/85">
        <article
          className="prose prose-invert max-w-none"
          style={{
            fontFamily: "'Pretendard Variable', 'Noto Sans KR', system-ui, sans-serif",
            letterSpacing: '-0.005em',
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: (props) => (
                <h1
                  className="text-white mb-6"
                  style={{ fontSize: 'clamp(32px,5vw,44px)', lineHeight: 1.15, letterSpacing: '-0.025em' }}
                  {...props}
                />
              ),
              h2: (props) => (
                <h2 className="text-white mt-12 mb-3 text-xl tracking-tight" {...props} />
              ),
              h3: (props) => (
                <h3 className="text-white/90 mt-7 mb-2 text-base" {...props} />
              ),
              p: (props) => <p className="my-3 leading-relaxed text-white/80" {...props} />,
              ul: (props) => <ul className="list-disc pl-6 my-3 space-y-1 text-white/80" {...props} />,
              ol: (props) => <ol className="list-decimal pl-6 my-3 space-y-1 text-white/80" {...props} />,
              li: (props) => <li className="leading-relaxed" {...props} />,
              hr: () => <hr className="my-9 border-white/10" />,
              a: (props) => (
                <a
                  className="text-[#ff8a80] underline underline-offset-[3px] hover:text-white"
                  target={props.href?.startsWith('http') ? '_blank' : undefined}
                  rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  {...props}
                />
              ),
              blockquote: (props) => (
                <blockquote
                  className="my-4 py-3 px-4 text-white/80"
                  style={{ background: 'rgba(255,255,255,0.04)', borderLeft: '3px solid rgba(255,89,77,0.6)' }}
                  {...props}
                />
              ),
              table: (props) => (
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-sm border-collapse" {...props} />
                </div>
              ),
              thead: (props) => <thead style={{ background: 'rgba(255,255,255,0.06)' }} {...props} />,
              th: (props) => (
                <th className="text-left align-top px-3 py-2.5 font-semibold text-white border border-white/10" {...props} />
              ),
              td: (props) => (
                <td className="text-left align-top px-3 py-2.5 text-white/80 border border-white/10" {...props} />
              ),
              code: (props) => (
                <code className="px-1.5 py-0.5 rounded bg-white/10 text-white/90 text-[0.9em]" {...props} />
              ),
            }}
          >
            {body}
          </ReactMarkdown>

          <hr className="my-12 border-white/10" />

          <p className="text-sm text-white/45">
            {effective === 'ko' ? (
              <>
                English version: <a className="text-[#ff8a80] underline" href={`/en/${slug}`}>rescoop.ai/en/{slug}</a>
              </>
            ) : (
              <>
                한국어판: <a className="text-[#ff8a80] underline" href={`/ko/${slug}`}>rescoop.ai/ko/{slug}</a>
              </>
            )}
          </p>
        </article>
      </main>
    </div>
  );
}
